import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Bug,
  Calendar,
  User,
  Globe,
  Monitor,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  History,
  Image as ImageIcon,
  ExternalLink,
  Save,
  Clock,
  Smartphone,
} from 'lucide-react';
import { BugReportRecord, BugStatus, BugAttachmentRecord } from '@/types/database';
import { bugService } from '@/services/bugService';
import { formatDate } from '@/lib/utils';

interface BugDetailDrawerProps {
  bug: BugReportRecord | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdated: () => void;
}

export const BugDetailDrawer: React.FC<BugDetailDrawerProps> = ({
  bug,
  isOpen,
  onClose,
  onUpdated,
}) => {
  const [currentBug, setCurrentBug] = useState<BugReportRecord | null>(bug);
  const [status, setStatus] = useState<BugStatus>('new');
  const [adminNotes, setAdminNotes] = useState<string>('');
  const [resolutionNotes, setResolutionNotes] = useState<string>('');
  const [statusNote, setStatusNote] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [activeImagePreview, setActiveImagePreview] = useState<{ url: string; name: string } | null>(null);

  // Load detailed bug with signed URLs whenever opened or bug changes
  useEffect(() => {
    if (isOpen && bug) {
      setError(null);
      setStatus(bug.status);
      setAdminNotes(bug.admin_notes || '');
      setResolutionNotes(bug.resolution_notes || '');
      setStatusNote('');

      // Fetch fresh detail with signed attachment URLs
      bugService.getBugReportById(bug.id).then((fresh) => {
        if (fresh) {
          setCurrentBug(fresh);
          setStatus(fresh.status);
          setAdminNotes(fresh.admin_notes || '');
          setResolutionNotes(fresh.resolution_notes || '');
        }
      });
    }
  }, [isOpen, bug]);

  if (!bug) return null;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      await bugService.updateBugStatus({
        bugId: bug.id,
        status,
        adminNotes: adminNotes.trim() || undefined,
        resolutionNotes: resolutionNotes.trim() || undefined,
        statusNote: statusNote.trim() || undefined,
      });

      onUpdated();
      onClose();
    } catch (err: any) {
      console.error('Failed to update bug report:', err);
      setError(err.message || 'Failed to update bug report.');
    } finally {
      setIsSaving(false);
    }
  };

  const getStatusBadge = (st: BugStatus) => {
    switch (st) {
      case 'new':
        return <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-blue-100 text-blue-800 border border-blue-200">New</span>;
      case 'reviewing':
        return <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-indigo-100 text-indigo-800 border border-indigo-200">Reviewing</span>;
      case 'fixing':
        return <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-amber-100 text-amber-800 border border-amber-200">Fixing</span>;
      case 'ready_to_verify':
        return <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-teal-100 text-teal-800 border border-teal-200">Ready to Verify</span>;
      case 'closed':
        return <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-slate-100 text-slate-700 border border-slate-200">Closed</span>;
    }
  };

  const deviceInfo = currentBug?.device_info || bug.device_info;
  const attachments: BugAttachmentRecord[] = currentBug?.attachments || bug.attachments || [];
  const statusHistory = currentBug?.status_history || bug.status_history || [];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader className="border-b border-slate-200 pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-lg bg-slate-900 text-white">
                <Bug className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <DialogTitle className="text-lg font-bold text-slate-900 font-mono">
                    {bug.bug_number}
                  </DialogTitle>
                  {getStatusBadge(bug.status)}
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500 mt-1 flex-wrap">
                  <span className="flex items-center gap-1">
                    <User className="w-3.5 h-3.5 text-slate-400" />
                    {bug.reporter_name || bug.reporter_email || 'Anonymous'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5 text-slate-400" />
                    {formatDate(bug.created_at)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </DialogHeader>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-5 pt-2">
          {/* User's Problem Description */}
          <div className="space-y-1.5 bg-slate-50 p-3.5 rounded-lg border border-slate-200">
            <Label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
              Reporter Description
            </Label>
            <p className="text-xs sm:text-sm text-slate-900 whitespace-pre-wrap leading-relaxed">
              {bug.description}
            </p>
          </div>

          {/* Screenshots Gallery */}
          {attachments.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                <ImageIcon className="w-3.5 h-3.5 text-slate-500" />
                Screenshots ({attachments.length})
              </Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {attachments.map((att) => (
                  <div
                    key={att.id}
                    onClick={() => att.signed_url && setActiveImagePreview({ url: att.signed_url, name: att.file_name })}
                    className="group border border-slate-200 rounded-lg overflow-hidden bg-white p-1 shadow-xs cursor-pointer hover:border-emerald-400 transition-colors"
                  >
                    <div className="h-28 bg-slate-100 rounded overflow-hidden flex items-center justify-center">
                      {att.signed_url ? (
                        <img
                          src={att.signed_url}
                          alt={att.file_name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      ) : (
                        <ImageIcon className="w-6 h-6 text-slate-400" />
                      )}
                    </div>
                    <div className="p-1 text-[10px] text-slate-600 truncate font-medium">
                      {att.file_name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Captured Diagnostic Context */}
          <div className="space-y-2 bg-slate-50/70 p-3 rounded-lg border border-slate-200">
            <Label className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
              <Monitor className="w-3.5 h-3.5 text-slate-500" />
              Technical Diagnostic Context
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-1.5">
                <span className="font-semibold text-slate-600">Route:</span>
                <code className="text-[11px] bg-slate-200/70 px-1.5 py-0.5 rounded text-slate-900 max-w-[200px] truncate">
                  {bug.app_route}
                </code>
              </div>
              {bug.entity_id && (
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold text-slate-600">Entity:</span>
                  <span className="text-[11px] text-slate-800">
                    {bug.entity_type || 'Record'} ({bug.entity_id.slice(0, 8)}...)
                  </span>
                </div>
              )}
              {deviceInfo?.browser && (
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold text-slate-600">Browser:</span>
                  <span className="text-slate-800">{deviceInfo.browser}</span>
                </div>
              )}
              {deviceInfo?.os && (
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold text-slate-600">OS:</span>
                  <span className="text-slate-800">{deviceInfo.os}</span>
                </div>
              )}
              {deviceInfo?.viewport && (
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold text-slate-600">Viewport:</span>
                  <span className="text-slate-800">{deviceInfo.viewport}</span>
                </div>
              )}
              {deviceInfo?.app_version && (
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold text-slate-600">App Build:</span>
                  <span className="text-slate-800">v{deviceInfo.app_version}</span>
                </div>
              )}
            </div>
          </div>

          {/* Admin Investigation & Resolution Controls */}
          <div className="space-y-3 pt-2 border-t border-slate-200">
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
              Investigation & Resolution
            </h3>

            {/* Status Dropdown */}
            <div className="space-y-1">
              <Label htmlFor="bug-status" className="text-xs font-semibold text-slate-700">
                Bug Lifecycle Status
              </Label>
              <select
                id="bug-status"
                value={status}
                onChange={(e) => setStatus(e.target.value as BugStatus)}
                className="w-full text-xs p-2.5 rounded-lg border border-slate-300 bg-white font-medium focus:ring-1 focus:ring-emerald-600 focus:border-emerald-600"
              >
                <option value="new">New (Reported)</option>
                <option value="reviewing">Reviewing (Investigating)</option>
                <option value="fixing">Fixing (In Progress)</option>
                <option value="ready_to_verify">Ready to Verify</option>
                <option value="closed">Closed (Resolved)</option>
              </select>
            </div>

            {/* Admin Notes */}
            <div className="space-y-1">
              <Label htmlFor="admin-notes" className="text-xs font-semibold text-slate-700">
                Investigation / Root Cause Notes
              </Label>
              <textarea
                id="admin-notes"
                rows={2}
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Developer analysis, identified cause, affected functions..."
                className="w-full text-xs p-2.5 rounded-lg border border-slate-300 focus:ring-1 focus:ring-emerald-600 text-slate-900 resize-none"
              />
            </div>

            {/* Resolution Notes */}
            <div className="space-y-1">
              <Label htmlFor="resolution-notes" className="text-xs font-semibold text-slate-700">
                Resolution / Fix Notes
              </Label>
              <textarea
                id="resolution-notes"
                rows={2}
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Summary of code change, fix applied, or verification instructions..."
                className="w-full text-xs p-2.5 rounded-lg border border-slate-300 focus:ring-1 focus:ring-emerald-600 text-slate-900 resize-none"
              />
            </div>

            {/* Status Change Note */}
            <div className="space-y-1">
              <Label htmlFor="status-note" className="text-xs font-semibold text-slate-700">
                Status Change Note <span className="text-[11px] text-slate-400 font-normal">(Optional audit log comment)</span>
              </Label>
              <input
                id="status-note"
                type="text"
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                placeholder="e.g. Verified fix on staging environment"
                className="w-full text-xs p-2 rounded-lg border border-slate-300 text-slate-900"
              />
            </div>
          </div>

          {/* Status History / Audit Trail */}
          {statusHistory.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-slate-200">
              <Label className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                <History className="w-3.5 h-3.5 text-slate-500" />
                Audit Trail ({statusHistory.length})
              </Label>
              <div className="space-y-1.5 max-h-36 overflow-y-auto text-xs">
                {statusHistory.map((item, idx) => (
                  <div
                    key={idx}
                    className="p-2 rounded bg-slate-50 border border-slate-200 flex flex-col gap-0.5"
                  >
                    <div className="flex items-center justify-between text-[11px] text-slate-500">
                      <span className="font-semibold text-slate-700">
                        {item.changed_by_name || 'Admin'}
                      </span>
                      <span>{formatDate(item.changed_at)}</span>
                    </div>
                    <div className="text-slate-800 font-medium text-[11px]">
                      {item.notes || `Status changed from ${item.from_status} to ${item.to_status}`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <DialogFooter className="pt-2 flex justify-end gap-2 border-t border-slate-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSaving}
              className="text-xs"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              className="bg-emerald-700 hover:bg-emerald-600 font-semibold text-xs gap-1.5 shadow-sm text-white"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <Save className="w-3.5 h-3.5" /> Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </form>

        {/* Full Image Preview Lightbox */}
        {activeImagePreview && (
          <Dialog open={Boolean(activeImagePreview)} onOpenChange={(open) => !open && setActiveImagePreview(null)}>
            <DialogContent className="max-w-4xl p-2 bg-slate-950 border-slate-800">
              <DialogHeader className="p-2 border-b border-slate-800 text-white flex flex-row items-center justify-between">
                <DialogTitle className="text-sm font-semibold truncate text-slate-200">
                  {activeImagePreview.name}
                </DialogTitle>
              </DialogHeader>
              <div className="max-h-[75vh] overflow-auto flex items-center justify-center p-2">
                <img
                  src={activeImagePreview.url}
                  alt={activeImagePreview.name}
                  className="max-w-full max-h-[70vh] object-contain rounded"
                />
              </div>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
};
